import { Organization, OrganizationMember as AuthOrganizationMember, Role } from 'auth0'
import { firstValueFrom, forkJoin, from, Observable, of, throwError } from 'rxjs'
import { catchError, finalize, map, switchMap, tap } from 'rxjs/operators'
import { environment } from 'src/environments/environment'

import { HttpClient } from '@angular/common/http'
import { Injectable, NgZone } from '@angular/core'
import { Router } from '@angular/router'
import { AppState, AuthService, RedirectLoginOptions, User } from '@auth0/auth0-angular'
import { EndpointOptions, RequestService } from '@services/request.service'
import { enterZone } from '@services/zone-aware.interceptor'

export type ModuleType = '' | 'admin' | 'assets' | 'billing' | 'builder' | 'files' | 'my-shifts' | 'oee' | 'projects' | 'model-editor' | 'models' | 'organization' | 'viewer'

export type AuthUser = User & {
    org_id?: string
}

const LAST_ORGANIZATION_STORAGE_KEY = 'kartorium_last_login_organization_id'
const AUTH_DIAGNOSTIC_EVENTS_KEY = 'kartorium_auth_diagnostic_events'
const AUTH_REDIRECT_FALLBACK_TARGET_KEY = 'kartorium_auth_redirect_fallback_target'
const LOGIN_ACCOUNT_SELECTION_STORAGE_KEY = 'kartorium_login_account_selection'

export type LoginAccountSelection =
    | { kind: 'personal' }
    | { kind: 'organization', organizationId: string }

export type PersonalAccountSummary = {
    accessStatus: 'active' | 'suspended' | 'ended'
}

export type UserAccounts = {
    canonicalUserID?: string
    authenticationSubject?: string
    organizations: Organization[]
    personalAccount: PersonalAccountSummary | null
    canMigrateExistingAccount?: boolean
}
const SSO_MIGRATION_TICKET_KEY = 'kartorium_sso_migration_ticket'

export type AuthTokenDiagnostic = {
    cacheKey: string
    expiresAt?: string
    hasRefreshToken: boolean
    issuedAt?: string
    orgId?: string
    scope?: string
    secondsRemaining?: number
    token: 'access' | 'backend' | 'id'
    ttlSeconds?: number
}

export type AuthDiagnosticEvent = {
    at: string
    detail?: Record<string, unknown>
    type: string
}

export type AuthDiagnostics = {
    currentUserOrgId?: string
    events: AuthDiagnosticEvent[]
    generatedAt: string
    rememberedOrganizationId: string | null
    tokens: AuthTokenDiagnostic[]
}

export type OrganizationMember = AuthOrganizationMember & {
    roles: Role[]
    kartoriumStaff?: boolean
    signInIdentities?: Array<{ provider: string, connection: string }>
}

export type OrganizationSsoSetupTicket = {
    expiresInSeconds: number
    ticket: string
}

export type OrganizationSsoProvider = 'google-apps' | 'waad'

export type OrganizationSsoProviderStatus = {
    provider: OrganizationSsoProvider
    connectionId?: string
    state: 'not_configured' | 'enabled' | 'disconnected' | 'needs_review'
    domains: string[]
    domainVerification: 'unknown'
    canDisconnect: boolean
}

export type OrganizationSignInPolicy = { revision: number; requireCompanySso: boolean; allowedMethods: { password: boolean; google: boolean; microsoft: boolean }; blockedConnectionIds: string[]; approvedCompanyConnectionIds: string[] }
export type OrganizationSignInConnection = { id: string; name: string; label: string; method: 'password' | 'google' | 'microsoft' | 'company-sso'; verified: boolean; verifiedAt?: string }
export type OrganizationSignInPolicyStatus = { policy: OrganizationSignInPolicy; connections: OrganizationSignInConnection[]; verifiedPaths: OrganizationSignInConnection[]; recovery: { configured: boolean }; audit: Array<{ action: string; actorSubject: string; revision: number; createdAt: string }> }
const ORGANIZATION_VERIFICATION_KEY = 'kartorium_pending_org_verification'

export type OrganizationSsoStatus = {
    policyStatus?: OrganizationSignInPolicyStatus
    providers: OrganizationSsoProviderStatus[]
    verification?: {
        organizationId: string
        currentSignIn: 'password' | 'google' | 'microsoft' | 'google-workspace' | 'microsoft-entra' | 'unknown'
        passwordConnection?: string
        ssoConnections: Array<{ provider: 'google-apps' | 'waad'; connectionName: string }>
    }
    passwordSignInRequired: boolean
    passwordSignIn?: { state: 'enabled' | 'disabled' | 'needs_review'; connectionEnabled?: boolean; canDisable: boolean; canEnable: boolean; reason?: string }
}

export const AuthRole = Object.freeze({
    /** Can view and edit projects */
    EDITOR: environment.auth.roles['editor'],
    /** Can view projects */
    READ_ONLY: environment.auth.roles['read only'],
    /** Can do anything */
    SUPERUSER: environment.auth.roles['super user'],
})

export const isAdminRole = (roleID: string): boolean => isSuperUser(roleID) || isEditor(roleID)
export const isEditor = (roleID: string): boolean => roleID == AuthRole.EDITOR
export const isReadOnly = (roleID: string): boolean => roleID == AuthRole.READ_ONLY
export const isSuperUser = (roleID: string): boolean => roleID == AuthRole.SUPERUSER

export const hasViewerAccess = (...roles: Role[]) => roles.some(role => isReadOnly(role.id) || hasBuilderAccess(role))
export const hasBuilderAccess = (...roles: Role[]) => roles.some(role => isEditor(role.id) || hasUserAdminAccess(role))
export const hasUserAdminAccess = (...roles: Role[]) => roles.some(role => isSuperUser(role.id))

export function isInteractiveAuthRequired(err: any): boolean {
    if (!err) return false
    const code = err.error || err.cause?.error
    const name = err.name || err.cause?.name || ''
    const message = [
        err.message,
        err.cause?.message,
        String(err),
        err.cause ? String(err.cause) : '',
    ].filter(Boolean).join(' ')
    const codes = ['consent_required', 'login_required', 'interaction_required', 'invalid_grant', 'missing_refresh_token']
    const nameHints = ['ConsentRequiredError', 'LoginRequiredError', 'InteractionRequiredAuthError']
    const messageHints = ['Consent required', 'Login required', 'Interaction required', 'Missing Refresh Token']
    return (typeof code === 'string' && codes.includes(code))
        || nameHints.some(n => name.includes(n))
        || messageHints.some(m => message.includes(m))
}

@Injectable({
    providedIn: 'root'
})
export class AuthenticationService {
    public currentUser: AuthUser
    private canonicalMember: { subject: string, organization?: string, id: string } | undefined
    /** Application ownership uses the server-resolved member; signed claims stay intact. */
    get appMemberID(): string | undefined {
        return this.canonicalMember?.subject === this.currentUser?.sub &&
            this.canonicalMember?.organization === this.currentUser?.org_id
            ? this.canonicalMember.id : this.currentUser?.sub
    }

    public setCanonicalMemberID(id: string, authenticationSubject: string, organization = this.currentUser?.org_id): void {
        if (typeof id === 'string' && id.length > 0 && authenticationSubject === this.currentUser?.sub && organization === this.currentUser?.org_id) {
            this.canonicalMember = { subject: authenticationSubject, organization, id }
        }
    }
    public fetchingResources: boolean = true
    public lastUpdated: Date
    public organizationMembers: OrganizationMember[] = []
    public organizationRoles: Role[] = []
    public projectPublicStatus: boolean = false
    public roles: Role[] = []

    get currentModule(): ModuleType {
        const url = this._router.url

        if (url.includes("/admin")) {
            return "admin"
        } else if (url.includes("billing")) {
            return "billing"
        } else if (url.includes("my-shifts")) {
            return "my-shifts"
        } else if (url.includes("/oee")) {
            return "oee"
        } else if (url.includes("models/edit")) {
            return "model-editor"
        } else if (url.includes("models")) {
            return "models"
        } else if (url.includes("assets")) {
            return "assets"
        } else if (url.includes("organization")) {
            return "organization"
        } else if (url.includes("files")) {
            return "files"
        } else if (url.includes("projects/edit")) {
            return "builder"
        } else if (/projects\/\d+/.test(url)) { // Check if the URL matches "projects/" followed by any
            return "viewer"
        } else if (url.includes("projects")) {
            return "projects"
        } else {
            return ""
        }
    }
    get id(): string {
        const pathParts = this._router.url.split("/")
        return pathParts[pathParts.length - 1]
    }
    readonly user$ = (this._authService.user$ as Observable<AuthUser>).pipe(enterZone(this._ngZone))
    readonly isLoading$ = this._authService.isLoading$.pipe(enterZone(this._ngZone))
    readonly isAuthenticated$ = this._authService.isAuthenticated$.pipe(enterZone(this._ngZone))
    // Stream of errors auth0-spa-js raises (silent refresh: consent_required,
    // login_required, interaction_required, invalid_grant, etc.). app.component
    // subscribes so Auth0 SDK failures and backend token-refresh failures use
    // the same interactive reauthentication path.
    readonly error$ = this._authService.error$.pipe(enterZone(this._ngZone))

    get hasOrganization() { return this.currentUser?.org_id != null }

    get hasBuilderAccess() { return this.hasOrganization ? hasBuilderAccess(...this.roles) : true }
    get hasUserAdminAccess() { return this.hasOrganization ? hasUserAdminAccess(...this.roles) : false }
    get isReadOnly() { return this.hasOrganization ? this.roles?.some(role => isReadOnly(role.id)) : true }
    get isSuperUser() { return this.hasOrganization ? this.roles?.some(role => isSuperUser(role.id)) : false }

    public loginRedirectURL: string

    constructor(
        private _authService: AuthService,
        private _http: HttpClient,
        private _ngZone: NgZone,
        private _requestService: RequestService,
        private _router: Router,
    ) {
        this.user$.subscribe(user => {
            if (user?.sub !== this.currentUser?.sub || user?.org_id !== this.currentUser?.org_id) this.canonicalMember = undefined
            this.currentUser = user
            if (user?.org_id) this.rememberLoginOrganization(user.org_id)
        })
        this.error$.subscribe(err => {
            if (err) this.recordAuthDiagnosticEvent('auth0_error', this.getAuthErrorDetail(err))
        })
        this.installAuthDiagnosticsWindowHelper()
    }

    public createAccessToken() {
        const url = `${environment.api}/auth/access-token`
        const options = { error: { operation: 'Create Access Token' } } as EndpointOptions

        return this._requestService.get<string>(url, options).pipe(catchError(error => {
            if (['ACCOUNT_ACCESS_MISSING', 'ACCOUNT_ACCESS_UNAVAILABLE', 'ACCOUNT_ACCESS_INACTIVE'].includes(error?.error?.code)) {
                this._router.navigate(['error', '402'])
            }
            return throwError(() => error)
        }))
    }

    // Don't want to require a token if in the viewer
    public getToken(): Observable<string> {
        if (this.currentModule == "viewer") {
            if (this.projectPublicStatus) { // Public project in Viewer
                return of(undefined)
            } else { // Project in builder or private project in viewer
                return this._authService.getAccessTokenSilently()
            }
        } else {
            return this._authService.getAccessTokenSilently()
        }
    }

    /** Always returns an authenticated access token, regardless of viewer/public context.
     *  Use this for operations that always require authentication (e.g., file uploads, edits). */
    public getAuthenticatedToken(): Observable<string> {
        return this._authService.getAccessTokenSilently()
    }

    public getUserAccounts(): Observable<UserAccounts> {
        const url = `${environment.api}/user/accounts`

        const organization = this.currentUser?.org_id
        return this._http.get<UserAccounts & { hasFreeTrial?: boolean }>(url).pipe(
            tap(accounts => this.setCanonicalMemberID(accounts.canonicalUserID, accounts.authenticationSubject, organization)),
            map(accounts => ({
            ...accounts,
            // Old backends omit personalAccount; explicit managed null remains authoritative.
            personalAccount: Object.prototype.hasOwnProperty.call(accounts, 'personalAccount')
                ? accounts.personalAccount ?? null
                : accounts.hasFreeTrial === true ? { accessStatus: 'active' as const } : null,
        })))
    }
    /** Keep a short-lived server-signed proof while Auth0 redirects to password sign-in. */
    public async migrateSsoToExistingPasswordAccount(): Promise<void> {
        const ssoAccessToken = await firstValueFrom(this._authService.getAccessTokenSilently())
        if (!ssoAccessToken) throw new Error('Your SSO sign-in could not be verified.')
        const result = await firstValueFrom(this._http.post<{ ticket: string }>(
            `${environment.api}/user/link-sso/start`, {},
            { headers: { Authorization: `Bearer ${ssoAccessToken}` } },
        ))
        sessionStorage.setItem(SSO_MIGRATION_TICKET_KEY, result.ticket)
        this.loginWithRedirect({
            appState: { target: 'login' },
            authorizationParams: {
                connection: 'Username-Password-Authentication',
                prompt: 'login',
                max_age: 0,
            },
        })
    }

    public async completePendingSsoMigration(): Promise<void> {
        const ticket = sessionStorage.getItem(SSO_MIGRATION_TICKET_KEY)
        if (!ticket) return
        sessionStorage.removeItem(SSO_MIGRATION_TICKET_KEY)
        const passwordClaims = await firstValueFrom(this._authService.idTokenClaims$)
        if (!passwordClaims?.sub?.startsWith('auth0|') || !passwordClaims.__raw ||
            passwordClaims['https://kartorium.com/authentication_strategy'] !== 'auth0') {
            throw new Error('Please sign in with your existing Kartorium password to connect the accounts.')
        }
        const passwordAccessToken = await firstValueFrom(this._authService.getAccessTokenSilently())
        try {
            await firstValueFrom(this._http.post<{ linked: boolean }>(`${environment.api}/user/link-sso`,
                { ticket, passwordIdToken: passwordClaims.__raw },
                { headers: { Authorization: `Bearer ${passwordAccessToken}` } }))
        } catch (error) {
            const failure = error as { status?: number, error?: { code?: string, error?: string } }
            if (failure?.status === 409 && failure.error?.code === 'sso_identity_conflict' &&
                typeof failure.error.error === 'string') {
                throw new Error(failure.error.error)
            }
            throw error
        }
    }

    public loginWithRedirect(options?: RedirectLoginOptions<AppState>) {
        this.rememberAuthRedirectFallbackTarget(options?.appState?.target)
        this.recordAuthDiagnosticEvent('login_redirect', {
            organization: options?.authorizationParams?.organization ?? null,
            target: options?.appState?.target ?? null,
        })
        this._authService.loginWithRedirect(options).subscribe({
            error: err => {
                this.recordAuthDiagnosticEvent('login_redirect_error', this.getAuthErrorDetail(err))
                console.error('Failed to start Auth0 login redirect:', err)
            },
        })
    }

    public consumeAuthRedirectFallbackTarget(): string | null {
        try {
            const target = sessionStorage.getItem(AUTH_REDIRECT_FALLBACK_TARGET_KEY)
            sessionStorage.removeItem(AUTH_REDIRECT_FALLBACK_TARGET_KEY)
            return target
        } catch {
            return null
        }
    }

    public loginWithRememberedOrganizationRedirect(target: string): void {
        const organization = this.getRememberedLoginOrganizationId()

        this.loginWithRedirect({
            appState: { target },
            authorizationParams: organization ? { organization } : undefined,
        })
    }

    public getAuthDiagnostics(): AuthDiagnostics {
        return {
            currentUserOrgId: this.currentUser?.org_id,
            events: this.getAuthDiagnosticEvents(),
            generatedAt: new Date().toISOString(),
            rememberedOrganizationId: this.getRememberedLoginOrganizationId(),
            tokens: this.getAuthTokenDiagnostics(),
        }
    }

    public recordAuthDiagnosticEvent(type: string, detail?: Record<string, unknown>): void {
        if (!this.isAuthDiagnosticsEnabled()) return

        try {
            const event: AuthDiagnosticEvent = { at: new Date().toISOString(), type, detail }
            const events = [event, ...this.getAuthDiagnosticEvents()].slice(0, 25)
            sessionStorage.setItem(AUTH_DIAGNOSTIC_EVENTS_KEY, JSON.stringify(events))
        } catch {
            // Diagnostics are best-effort only.
        }
    }

    public getRememberedLoginOrganization(organizations: Organization[]): Organization | undefined {
        const organizationId = this.getRememberedLoginOrganizationId()
        if (!organizationId) return undefined

        const organization = organizations.find(org => org.id == organizationId)
        if (!organization) this.clearRememberedLoginOrganization()

        return organization
    }

    public rememberLoginOrganization(organizationId?: string): void {
        if (!organizationId) {
            this.clearRememberedLoginOrganization()
            return
        }

        try {
            localStorage.setItem(LAST_ORGANIZATION_STORAGE_KEY, organizationId)
        } catch {
            // localStorage can be unavailable in hardened embedded browsers.
        }

        try {
            document.cookie = `${LAST_ORGANIZATION_STORAGE_KEY}=${encodeURIComponent(organizationId)}; path=/; max-age=31536000; SameSite=Lax`
        } catch {
            // document.cookie can also be blocked in hardened embedded browsers.
        }

        this.recordAuthDiagnosticEvent('remember_organization', { organization: organizationId })
    }

    public rememberLoginAccount(selection: LoginAccountSelection): void {
        try {
            sessionStorage.setItem(LOGIN_ACCOUNT_SELECTION_STORAGE_KEY, JSON.stringify(selection))
        } catch {
            // sessionStorage can be unavailable in hardened embedded browsers.
        }

        if (selection.kind === 'organization') {
            this.rememberLoginOrganization(selection.organizationId)
        } else {
            this.clearRememberedLoginOrganization()
        }
    }

    public getLoginAccountSelection(): LoginAccountSelection | null {
        let value: unknown
        try {
            value = JSON.parse(sessionStorage.getItem(LOGIN_ACCOUNT_SELECTION_STORAGE_KEY) ?? 'null')
        } catch {
            return null
        }

        if (typeof value !== 'object' || value === null || !('kind' in value)) return null
        if (value.kind === 'personal') return { kind: 'personal' }
        if (value.kind === 'organization'
            && 'organizationId' in value
            && typeof value.organizationId === 'string'
            && value.organizationId.length > 0) {
            return { kind: 'organization', organizationId: value.organizationId }
        }
        return null
    }

    public clearRememberedLoginOrganization(): void {
        try {
            localStorage.removeItem(LAST_ORGANIZATION_STORAGE_KEY)
        } catch {
            // localStorage can be unavailable in hardened embedded browsers.
        }

        try {
            document.cookie = `${LAST_ORGANIZATION_STORAGE_KEY}=; path=/; max-age=0; SameSite=Lax`
        } catch {
            // document.cookie can also be blocked in hardened embedded browsers.
        }

        this.recordAuthDiagnosticEvent('clear_remembered_organization')
    }

    private getRememberedLoginOrganizationId(): string | null {
        try {
            return localStorage.getItem(LAST_ORGANIZATION_STORAGE_KEY) ?? this.getRememberedLoginOrganizationCookie()
        } catch {
            return this.getRememberedLoginOrganizationCookie()
        }
    }

    private getRememberedLoginOrganizationCookie(): string | null {
        const cookie = document.cookie
            .split('; ')
            .find(row => row.startsWith(`${LAST_ORGANIZATION_STORAGE_KEY}=`))
        if (!cookie) return null

        return decodeURIComponent(cookie.split('=').slice(1).join('='))
    }

    private getAuthDiagnosticEvents(): AuthDiagnosticEvent[] {
        try {
            return JSON.parse(sessionStorage.getItem(AUTH_DIAGNOSTIC_EVENTS_KEY) ?? '[]')
        } catch {
            return []
        }
    }

    private rememberAuthRedirectFallbackTarget(target?: string): void {
        if (!target) return

        try {
            sessionStorage.setItem(AUTH_REDIRECT_FALLBACK_TARGET_KEY, target)
        } catch {
            // Session storage can be unavailable in hardened embedded browsers.
        }
    }

    private getAuthErrorDetail(err: any): Record<string, unknown> {
        return {
            code: err?.error ?? err?.cause?.error ?? null,
            message: err?.message ?? err?.cause?.message ?? null,
            name: err?.name ?? err?.cause?.name ?? null,
        }
    }

    private getAuthTokenDiagnostics(): AuthTokenDiagnostic[] {
        const tokens: AuthTokenDiagnostic[] = []

        try {
            Object.keys(localStorage)
                .filter(key => key.startsWith('@@auth0spajs@@'))
                .forEach(key => {
                    const value = JSON.parse(localStorage.getItem(key) ?? '{}')
                    const cacheBody = value?.body ?? value
                    const hasRefreshToken = !!cacheBody?.refresh_token

                    if (cacheBody?.access_token) {
                        tokens.push(this.getTokenDiagnostic('access', key, cacheBody.access_token, hasRefreshToken))
                    }
                    if (cacheBody?.id_token) {
                        tokens.push(this.getTokenDiagnostic('id', key, cacheBody.id_token, hasRefreshToken))
                    }
                })
        } catch {
            // Keep diagnostics available even if one storage entry is malformed.
        }

        const backendAccessToken = localStorage.getItem('accessToken')
        if (backendAccessToken) {
            tokens.push(this.getTokenDiagnostic('backend', 'accessToken', backendAccessToken, false))
        }

        return tokens
    }

    private getTokenDiagnostic(token: 'access' | 'backend' | 'id', cacheKey: string, jwt: string, hasRefreshToken: boolean): AuthTokenDiagnostic {
        const payload = this.decodeJwtPayload(jwt)
        const nowSeconds = Math.floor(Date.now() / 1000)

        return {
            cacheKey,
            expiresAt: payload?.exp ? new Date(payload.exp * 1000).toISOString() : undefined,
            hasRefreshToken,
            issuedAt: payload?.iat ? new Date(payload.iat * 1000).toISOString() : undefined,
            orgId: payload?.org_id,
            scope: payload?.scope,
            secondsRemaining: payload?.exp ? payload.exp - nowSeconds : undefined,
            token,
            ttlSeconds: payload?.exp && payload?.iat ? payload.exp - payload.iat : undefined,
        }
    }

    private decodeJwtPayload(jwt: string): any {
        try {
            const payload = jwt.split('.')[1]
            const normalizedPayload = payload.replace(/-/g, '+').replace(/_/g, '/')
                .padEnd(Math.ceil(payload.length / 4) * 4, '=')
            return JSON.parse(atob(normalizedPayload))
        } catch {
            return {}
        }
    }

    private installAuthDiagnosticsWindowHelper(): void {
        if (!this.isAuthDiagnosticsEnabled()) return

        try {
            (window as any).kartoriumAuthDiagnostics = () => this.getAuthDiagnostics()
        } catch {
            // Diagnostics helper is best-effort only.
        }
    }

    private isAuthDiagnosticsEnabled(): boolean {
        if (environment.production) return false

        try {
            return ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname)
        } catch {
            return false
        }
    }

    public logout() {
        try {
            sessionStorage.removeItem(LOGIN_ACCOUNT_SELECTION_STORAGE_KEY)
        } catch {
            // sessionStorage can be unavailable in hardened embedded browsers.
        }
        this.clearRememberedLoginOrganization()
        this._authService.logout({ logoutParams: { returnTo: environment.logoutRedirect } })
    }

    /** Fetches things like an Organization's Roles  */
    public getOrganizationResources() {
        this.fetchingResources = true

        return forkJoin([this.getRoles(), this.getFilteredOrganizationMembers()]).pipe(
            tap(([roles, members]) => {
                const userIsSuperUser = roles?.some(role => isSuperUser(role.id))

                if (!userIsSuperUser) { // Removes superuser role from list of roles if user is not a superuser
                    roles.splice(roles.findIndex(role => isSuperUser(role.id)), 1)
                }

                let approvedRoles = []
                roles.forEach(role => {
                    if (role.name == 'read only' || role.name == 'editor' || role.name == 'super user') approvedRoles.push(role)
                })

                // Sorts roles by how much they are authorized to do
                this.organizationRoles = approvedRoles.sort((a, b) => a.description.localeCompare(b.description))
                this.organizationMembers = members
                this.lastUpdated = new Date()
            }),
            finalize(() => this.fetchingResources = false)
        )
    }

    /** ORGANIZATION */

    public getOrganization() {
        const url = `${environment.api}/org`
        const options = { error: { operation: 'Get Organization', toast: true } } as EndpointOptions

        return this._requestService.get<Organization>(url, options).pipe(
            tap(organization => {
                if (organization.metadata == null) {
                    organization.metadata = { branding: {} }
                }
            })
        )
    }

    public checkOrganizationName(name: string) {
        const url = `${environment.api}/org/check-name?name=${name}`
        const options = { error: { operation: 'Check Organization Name', toast: true } } as EndpointOptions

        return this._requestService.get<{ available: boolean, message: string }>(url, options)
    }

    public updateOrganization(organization: Organization) {
        const url = `${environment.api}/org`
        const options = {
            successToast: { title: `Organization Updated`, message: `Your changes have been saved.` },
            error: { operation: 'Update Organization', toast: true }
        } as EndpointOptions

        return this._requestService.update<Organization>(url, organization, options)
    }

    public createOrganizationSsoSetupTicket(provider: OrganizationSsoProvider) {
        const url = `${environment.api}/org/sso/setup-ticket`
        const options: EndpointOptions = {
            error: { operation: 'Create SSO Setup Link', toast: true },
        }

        return this._requestService.create<OrganizationSsoSetupTicket>(url, { provider }, options)
    }

    public getOrganizationSsoStatus() {
        return this._requestService.get<OrganizationSsoStatus>(`${environment.api}/org/sso/status`, {
            error: { operation: 'Read organization SSO status', toast: true, throwError: true },
        })
    }

    private async organizationIdProof(): Promise<string> {
        const claims = await firstValueFrom(this._authService.idTokenClaims$)
        if (!claims?.__raw) throw { status: 428, error: { code: 'reauthentication_required', message: 'Authenticate again before changing organization sign-in.' } }
        return claims.__raw
    }

    public setOrganizationPasswordSignIn(enabled: boolean) {
        return from(this.organizationIdProof()).pipe(switchMap(idToken => this._requestService.update<void>(`${environment.api}/org/sso/password-sign-in`, { enabled, idToken }, {
            error: { operation: 'Change organization password sign-in', toast: true, throwError: true },
        })))
    }

    public disconnectOrganizationSso(connectionId: string) {
        return from(this.organizationIdProof()).pipe(switchMap(idToken => this._requestService.delete<void>(`${environment.api}/org/sso/connections/${encodeURIComponent(connectionId)}`, {
            httpOptions: { body: { idToken } }, error: { operation: 'Disconnect organization SSO', toast: true, throwError: true },
        })))
    }

    public saveOrganizationSignInPolicy(policy: Pick<OrganizationSignInPolicy, 'revision' | 'requireCompanySso' | 'allowedMethods'>) {
        return from(this.organizationIdProof()).pipe(switchMap(idToken => this._requestService.update<void>(`${environment.api}/org/sso/policy`, { ...policy, idToken }, {
            error: { operation: 'Save organization sign-in policy', toast: true, throwError: true },
        })))
    }

    public enableOrganizationSso(connectionId: string) {
        return from(this.organizationIdProof()).pipe(switchMap(idToken => this._requestService.create<void>(`${environment.api}/org/sso/connections/${encodeURIComponent(connectionId)}/enable`, { idToken }, {
            error: { operation: 'Enable organization SSO', toast: true, throwError: true },
        })))
    }

    public generateOrganizationRecoveryCode() {
        return from(this.organizationIdProof()).pipe(switchMap(idToken => this._requestService.create<{ code: string }>(`${environment.api}/org/sso/recovery-code`, { idToken }, {
            error: { operation: 'Generate organization recovery code', toast: true, throwError: true },
        })))
    }

    public recoverOrganizationSignIn(code: string, organizationId?: string) {
        return from(this.organizationIdProof()).pipe(switchMap(idToken => this._requestService.create<any>(`${environment.api}/org/sso/recover`, { idToken, code, ...(organizationId ? { organizationId } : {}) }, {
            error: { operation: 'Recover organization sign-in', toast: false, throwError: true },
        })))
    }

    public startOrganizationSignInVerification(organizationId: string, connectionName: string): void {
        sessionStorage.setItem(ORGANIZATION_VERIFICATION_KEY, JSON.stringify({ organizationId, connectionName }))
        this.loginWithRedirect({ appState: { target: `/organization-access?organizationId=${encodeURIComponent(organizationId)}` }, authorizationParams: { organization: organizationId, connection: connectionName, prompt: 'login', max_age: 0 } })
    }

    public async completeOrganizationSignInVerification(): Promise<boolean> {
        const marker = sessionStorage.getItem(ORGANIZATION_VERIFICATION_KEY)
        if (!marker) return false
        sessionStorage.removeItem(ORGANIZATION_VERIFICATION_KEY)
        let expected: { organizationId: string; connectionName: string }
        try { expected = JSON.parse(marker) } catch { throw new Error('Verification could not be resumed. Please verify sign-in again.') }
        const claims = await firstValueFrom(this._authService.idTokenClaims$)
        if (!claims?.__raw || claims.org_id !== expected.organizationId || claims['https://kartorium.com/connection_name'] !== expected.connectionName) {
            throw new Error('The returned sign-in does not match the requested organization connection. Please verify again.')
        }
        await firstValueFrom(this._requestService.create<void>(`${environment.api}/org/sso/verification`, { idToken: claims.__raw }, {
            error: { operation: 'Verify organization sign-in', toast: false, throwError: true },
        }))
        return true
    }

    public getOrganizationMembers() {
        const url = `${environment.api}/org/members`
        const options = { error: { operation: "Get Organization's Members", toast: true } } as EndpointOptions

        return this._requestService.get<OrganizationMember[]>(url, options).pipe(
            tap(members => this.organizationMembers = members)
        )
    }

    /** Important Note: Filtered members exclude our special Kartorium Admins/Superusers */
    public getFilteredOrganizationMembers() {
        const url = `${environment.api}/org/members/filtered`
        const options = { error: { operation: "Get Organization's Members", toast: true } } as EndpointOptions

        return this._requestService.get<OrganizationMember[]>(url, options).pipe(
            tap(members => this.organizationMembers = members)
        )
    }

    /** MEMBERS */
    public addMemberToOrganization(name: string, email: string, roles: string[]) {
        const url = `${environment.api}/org/member`
        const options = {
            successToast: { title: `Member Added to Organization`, message: `Invitation has been sent to ${email}.` },
            error: { operation: 'Add Member to Organization', toast: true }
        } as EndpointOptions
        const member = { name, email, roles } as OrganizationMember

        return this._requestService.create<User>(url, member, options)
    }

    public removeMembersFromOrganization(memberIDs: string[]) {
        const url = `${environment.api}/org/members`
        const options = {
            successToast: { title: `Member Removed`, message: `Member has been successfully removed from your organization.` },
            error: { operation: 'Remove Members from Organization', toast: true },
            httpOptions: {
                body: memberIDs
            },
        } as EndpointOptions

        return this._requestService.delete<OrganizationMember[]>(url, options)
    }

    public getCurrentUsersRoles$() {
        const url = `${environment.api}/org/user/roles`
        const options = { error: { operation: 'Get Current Users Roles', retry: true, toast: true } } as EndpointOptions

        return this._requestService.get<Role[]>(url, options).pipe(
            tap(roles => this.roles = roles)
        )
    }

    /** ROLES */

    public getRoles() {
        const url = `${environment.api}/org/roles`
        const options = { error: { operation: 'Get Roles', toast: true } } as EndpointOptions

        return this._requestService.get<Role[]>(url, options)
    }

    public getMembersRoles(memberID: string) {
        const url = `${environment.api}/org/roles/${memberID}`
        const options = { error: { operation: "Get Member's Roles", toast: true } } as EndpointOptions

        return this._requestService.get<Role[]>(url, options)
    }

    public setMemberRole(memberID: string, roleID: string) {
        const url = `${environment.api}/org/member/role/${memberID}`
        const options = {
            successToast: { title: `Updated Member's Role`, message: `Member's role has been changed` },
            error: { operation: "Set Member's Role", toast: true, status: { message: "Member's role failed to update" } }
        } as EndpointOptions

        return this._requestService.update<any>(url, { roleID }, options)
    }

    public addMemberRoles(user: OrganizationMember, ...roleIDs: string[]) {
        const url = `${environment.api}/org/roles/${user.user_id}`
        const options = { error: { operation: "Add Member Roles", toast: true } } as EndpointOptions

        return this._requestService.create<Role[]>(url, roleIDs, options)
    }

    public removeMemberRoles(user: OrganizationMember, ...roleIDs: string[]) {
        const url = `${environment.api}/org/roles/${user.user_id}`
        const options = { error: { operation: "Remove Member Roles", toast: true } } as EndpointOptions

        return this._requestService.update<Role[]>(url, roleIDs, options)
    }

    public addMultipleMemberRoles(...members: { member: OrganizationMember, roleIDs: string[] }[]) {
        const url = `${environment.api}/org/members/roles`
        const options = { error: { operation: "Add Roles to Members", toast: true } } as EndpointOptions

        return this._requestService.create<OrganizationMember[]>(url, members, options)
    }

    public removeMultipleMemberRoles(...members: { member: OrganizationMember, roleIDs: string[] }[]) {
        const url = `${environment.api}/org/members/roles`
        const options = { error: { operation: "Remove Roles from Members", toast: true } } as EndpointOptions

        return this._requestService.update<OrganizationMember[]>(url, members, options)
    }
}
